import { Link } from "@tanstack/react-router";
import { createContext, useContext } from "react";

/** The two client-portal experiences. Today the URL decides the role — the viewer tree lives at the
 *  root paths, the admin tree under `/admin-view` — so both can be previewed side by side without an
 *  auth system. When real auth lands, this same flag just gets driven by the session instead of the
 *  path, and nothing downstream has to change (audit C2). */
export type ClientRole = "viewer" | "admin";

/** Path prefix that carries the admin experience of the client surfaces. */
export const ADMIN_VIEW_PREFIX = "/admin-view";

const ClientRoleContext = createContext<ClientRole>("viewer");

export function ClientRoleProvider({ role, children }: { role: ClientRole; children: React.ReactNode }) {
  return <ClientRoleContext.Provider value={role}>{children}</ClientRoleContext.Provider>;
}

export function useClientRole(): ClientRole {
  return useContext(ClientRoleContext);
}

/** "" for the viewer tree, `/admin-view` for the admin tree — the segment RoleLink prepends. */
export function useRolePrefix(): "" | typeof ADMIN_VIEW_PREFIX {
  return useClientRole() === "admin" ? ADMIN_VIEW_PREFIX : "";
}

/** The client-surface route paths, all relative to the current role's tree. */
export type RolePath = "/" | "/csuite" | "/vp-director" | "/general" | "/activity" | "/search/$searchId";

interface RoleLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: RolePath;
  params?: { searchId: string } | { candidateId: string };
  search?: { search?: string; from?: string };
  // Forwarded to the router's active matcher. Root-ish targets ("/" → "/admin-view") must pass
  // `{ exact: true }`, else the default fuzzy prefix-match marks them active on every sub-page.
  activeOptions?: { exact?: boolean };
}

/** A `<Link>` that keeps you inside the current role's tree: in the viewer tree it points at the root
 *  paths, in the admin tree it prepends `/admin-view`. Both trees register the same path suffixes, so
 *  the computed target is always a real route — the casts just opt out of the router's per-tree
 *  inference (the runtime `to` + `params` are valid either way). */
export function RoleLink({ to, params, search, activeOptions, ...rest }: RoleLinkProps) {
  const prefix = useRolePrefix();
  const target = to === "/" ? prefix || "/" : `${prefix}${to}`;
  return (
    <Link
      to={target as never}
      params={params as never}
      search={search as never}
      activeOptions={activeOptions as never}
      {...rest}
    />
  );
}

/** Strip the admin-view prefix off a pathname to get its viewer-tree equivalent. */
function toViewerPath(pathname: string): string {
  if (pathname === ADMIN_VIEW_PREFIX) return "/";
  if (pathname.startsWith(`${ADMIN_VIEW_PREFIX}/`)) return pathname.slice(ADMIN_VIEW_PREFIX.length);
  return pathname;
}

/** The same page in the other role's tree — so flipping roles keeps you on the role/candidate you
 *  were looking at (search params like `?search=` are dropped, which is fine for a role switch). */
// The viewer tree only mounts these route shapes; an admin-only page (team, activity, …) has no viewer
// equivalent, so flipping to Viewer there lands on the home overview rather than a dead route.
const VIEWER_ROUTES = /^\/(?:$|csuite|vp-director|general|activity|search\/)/;

/** The equivalent path in the other role's tree — so flipping role-view keeps you on the same page. Used by
 *  the account menu's View-as switch. (Viewer-only route shapes fall back to the overview when no equivalent
 *  exists.) */
export function roleCounterpart(pathname: string, target: ClientRole): string {
  const viewerPath = toViewerPath(pathname);
  if (target === "viewer") return VIEWER_ROUTES.test(viewerPath) ? viewerPath : "/";
  return viewerPath === "/" ? ADMIN_VIEW_PREFIX : `${ADMIN_VIEW_PREFIX}${viewerPath}`;
}
